/**
 * Reading guest scope off the session itself.
 *
 * A guest link — and an admin's guest preview — mints a *real* client session:
 * same cookie, same refresh, same role. What makes it a guest session is a pair
 * of claims the backend stamps on the access token, `guestQuoteId` and (for a
 * preview) `guestPreview`. Refresh rotation re-stamps them deliberately, so the
 * session cannot age into a full org session.
 *
 * That makes the token the only honest place to ask "is this a guest?". The
 * sessionStorage marker in `guest-session.ts` cannot answer it: cookies are
 * shared by every tab on the origin, the marker is not, so a second tab
 * restores the very same guest session and finds no marker to scope it by. That
 * is how a guest — or a previewing admin — ended up looking at the whole
 * platform.
 *
 * Decoded, never verified. This is UI scoping: it decides which screens to
 * draw, and a forged claim would only mislead the browser that forged it. What
 * a guest may actually READ is the backend's to enforce.
 */

interface GuestScope {
	/** The one quote this session is for. */
	quoteId: string;
	/** True for an admin's read-only preview of the guest view. */
	preview: boolean;
}

interface GuestClaims {
	guest?: boolean;
	guestQuoteId?: string;
	guestPreview?: boolean;
}

/** The JWT payload, or null if it isn't one. */
function decodeClaims(token: string): GuestClaims | null {
	try {
		const payload = token.split('.')[1];
		if (!payload) return null;
		const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
		// The payload is UTF-8; atob yields Latin-1, so re-decode to keep any
		// non-ASCII claim intact rather than mangling it.
		const text = decodeURIComponent(
			json
				.split('')
				.map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
				.join('')
		);
		return JSON.parse(text) as GuestClaims;
	} catch {
		return null;
	}
}

/**
 * The quote this session is confined to, or `null` for a normal client session.
 *
 * Reads `guestQuoteId` rather than the `guest` flag: the id is what the scope
 * actually is, and the backend only ever sets one alongside the other.
 */
export function readGuestScope(token: string | null | undefined): GuestScope | null {
	if (!token) return null;
	const claims = decodeClaims(token);
	if (!claims?.guestQuoteId) return null;
	return { quoteId: claims.guestQuoteId, preview: claims.guestPreview === true };
}
